import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";

import { data, ROWS, COLUMNS } from "@/utils/constants";
import { RootState } from "@/store/store";
import useCreateNewEntity from "@/hooks/useCreateNewEntity";
import { dateFormatter } from "@/utils/dateFormatter";

import "./ToothTable.scss";

function ToothTable({ notes }: { notes: string }) {
    const selectedFormulaData = useSelector(
        (state: RootState) => state.patients.selectedDentalFormula,
    );
    const isSelectedDefnalFormula = useSelector(
        (state: RootState) => state.patients.isSelectedDefnalFormula,
    );

    const baseFormula = isSelectedDefnalFormula
        ? selectedFormulaData[0].formula
        : data.formula;

    const [tableData, setTableData] = useState<{ [key: string]: string }>(
        Object.fromEntries(
            Object.entries(baseFormula).map(([k, v]) => [k, v.toString()]),
        ),
    );

    const protectedCells = useMemo(
        () =>
            Object.entries(baseFormula)
                .filter(([_, value]) => value.toString().trim() !== "")
                .map(([key]) => key),
        [baseFormula],
    );

    const { handleCreate } = useCreateNewEntity();
    const isLocalLoading = useSelector(
        (state: RootState) => state.ui.isLocalLoading,
    );
    const patientId = useSelector(
        (state: RootState) => state.patients.patientId,
    );
    const globalDate = useSelector(
        (state: RootState) => state.schedule.globalDate,
    );

    function handleChange(row: number, col: number, value: string) {
        const key = `${row}-${col}`;
        if (protectedCells.includes(key)) return;
        if (value.length > 3) return;

        setTableData(prev => ({ ...prev, [key]: value }));
    }

    const stringifiedFormula = Object.fromEntries(
        Object.entries(tableData).map(([key, value]) => [
            key.toString(),
            value.toString(),
        ]),
    );

    const newFormula = {
        date: dateFormatter(new Date(globalDate)),
        patientId: patientId || "",
        formula: stringifiedFormula,
        notes: notes,
    };

    async function createFormula() {
        await handleCreate({
            entity: "patients/dentalformula",
            data: newFormula,
            message: "Зубну формулу успішно створено",
        });
    }

    return (
        <div className='table-container'>
            <table className='custom-table'>
                <tbody>
                    {Array.from({ length: ROWS }).map((_, rowIndex) => (
                        <tr key={rowIndex}>
                            {Array.from({ length: COLUMNS }).map(
                                (_, colIndex) => {
                                    const key = `${rowIndex}-${colIndex}`;
                                    const isProtected =
                                        protectedCells.includes(key);

                                    return (
                                        <td
                                            key={colIndex}
                                            className='disabled'
                                            style={{
                                                borderRight:
                                                    colIndex ===
                                                    Math.floor(COLUMNS / 2 - 1)
                                                        ? "3px solid black"
                                                        : "none",
                                                width: "50px",
                                                borderBottom:
                                                    rowIndex ===
                                                    Math.floor(ROWS / 2 - 1)
                                                        ? "3px solid black"
                                                        : "none",
                                                cursor: isProtected
                                                    ? "not-allowed"
                                                    : "text",
                                                pointerEvents: isProtected
                                                    ? "none"
                                                    : "auto",
                                            }}
                                        >
                                            <input
                                                className={`tooth-input ${
                                                    isProtected
                                                        ? "tooth-input--readonly"
                                                        : ""
                                                }`}
                                                type='text'
                                                maxLength={3}
                                                value={tableData[key] || ""}
                                                readOnly={isProtected}
                                                onChange={e =>
                                                    handleChange(
                                                        rowIndex,
                                                        colIndex,
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </td>
                                    );
                                },
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>

            {!isSelectedDefnalFormula && (
                <div className='drawer_title_container button_container'>
                    <button
                        className='clear-button'
                        onClick={() => setTableData({ ...data.formula })}
                    >
                        Очистити
                    </button>
                    <button className='save-button' onClick={createFormula}>
                        {!isLocalLoading ? (
                            "Зберегти"
                        ) : (
                            <img
                                src='/animations/spinner-gif.gif'
                                alt='Зберігається...'
                                width='30'
                                height='30'
                            />
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}

export default React.memo(ToothTable);
